import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Map, Package, Archive, Users, Truck, Database, UserCog, User, LogOut, FileText, Menu, X, Smartphone } from 'lucide-react';
import useAppStore from '../store';
import { supabase } from '../lib/supabase';
import AppUpdateNotification from './AppUpdateNotification';

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, isAuthenticated } = useAppStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const isActive = (path: string) => location.pathname === path;

  // Check session validity on component mount
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const checkSession = async () => {
      try {
        // If not authenticated, redirect to login
        if (!isAuthenticated || !currentUser) {
          console.log('Non authentifié, redirection vers la page de connexion');
          navigate('/login', { replace: true });
          return;
        }

        const { data, error } = await supabase.auth.getUser();
        if (error || !data?.user) {
          // Invalid session - force logout and redirect
          console.log('Session invalide, déconnexion forcée');
          await logout();
          navigate('/login', { replace: true });
          return;
        }

        // Update last_active timestamp periodically
        const updateLastActive = async () => {
          try {
            await supabase
              .from('users')
              .update({ last_active: new Date().toISOString() })
              .eq('id', currentUser.id);
          } catch (error) {
            console.error('Error updating last_active:', error);
          }
        };

        // Update immediately and then every 5 minutes
        updateLastActive();
        const intervalId = setInterval(updateLastActive, 5 * 60 * 1000);

        cleanup = () => clearInterval(intervalId);
      } catch (error) {
        console.error('Session check error:', error);
        // On error, clear everything and redirect
        await logout();
        navigate('/login', { replace: true });
      }
    };

    checkSession();

    return () => {
      if (cleanup) cleanup();
    };
  }, [isAuthenticated, currentUser, navigate, logout]);

  const handleLogout = async () => {
    try {
      // Call the logout function
      await logout();
      
      // Force navigation to login page
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      // Even if there's an error, redirect
      navigate('/login', { replace: true });
    }
  };
  
  const menuItems = [
    { path: '/', icon: <Package className="h-5 w-5" />, label: 'Accueil' },
    { path: '/map', icon: <Map className="h-5 w-5" />, label: 'Carte' },
    { path: '/tasks', icon: <Package className="h-5 w-5" />, label: 'Tâches' },
    { path: '/archives', icon: <Archive className="h-5 w-5" />, label: 'Archives' },
    { path: '/clients', icon: <Users className="h-5 w-5" />, label: 'Clients' },
    { path: '/positions', icon: <Map className="h-5 w-5" />, label: 'Positions', logisticsOnly: true },
    { path: '/dumpsters', icon: <Package className="h-5 w-5" />, label: 'Bennes' },
    { path: '/depots', icon: <Database className="h-5 w-5" />, label: 'Dépôts', adminOnly: true },
    { path: '/users', icon: <UserCog className="h-5 w-5" />, label: 'Utilisateurs', adminOnly: true },
    { path: '/app-versions', icon: <Smartphone className="h-5 w-5" />, label: 'Versions APK', adminOnly: true },
    { path: '/logs', icon: <FileText className="h-5 w-5" />, label: 'Logs', adminOnly: true },
  ];

  // Close sidebar when route changes (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden bg-blue-800 text-white p-4 flex justify-between items-center z-50">
        <h1 className="text-xl font-bold">AppliGerbe</h1>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Overlay for mobile when sidebar is open */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop always visible, Mobile conditionally visible */}
      <div 
        className={`fixed md:static w-64 bg-blue-800 text-white h-screen md:h-auto flex flex-col z-50 transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        {/* Logo - Hidden on mobile when sidebar is open (already shown in header) */}
        <div className="p-4 border-b border-blue-700 hidden md:block">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">AppliGerbe <span className="text-sm text-blue-200 ml-2">v2.9.0</span></h1>
              {currentUser && (
                <p className="text-sm text-blue-200 mt-1 truncate">{currentUser.email}</p>
              )}
            </div>
            {currentUser?.role !== 'admin' && currentUser?.role !== 'logistics' && (
              <AppUpdateNotification />
            )}
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {menuItems.map(item => {
              if (item.adminOnly && currentUser?.role !== 'admin') return null;
              if ((item as any).logisticsOnly && currentUser?.role !== 'admin' && currentUser?.role !== 'logistics') return null;

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                      isActive(item.path)
                        ? 'bg-blue-700 text-white'
                        : 'text-blue-100 hover:bg-blue-700'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Profile and Logout */}
        <div className="p-4 border-t border-blue-700 space-y-2">
          <Link
            to="/profile"
            className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
              isActive('/profile')
                ? 'bg-blue-700 text-white'
                : 'text-blue-100 hover:bg-blue-700'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <User className="h-5 w-5" />
            <span>Profil</span>
          </Link>

          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors text-blue-100 hover:bg-blue-700"
          >
            <LogOut className="h-5 w-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;